import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { UnifiedLogger } from '@rbx/unified-logger';
import { useMediaQuery } from '@rbx/ui';
import { TBuildTarget, TProductKey, TRobloxEnvironment, TTargetEnvironment } from '../types';
import { getNavigationIXPParams, TNavigationIXPResults } from '../utils/getNavigationIXPParams';
import NavigationConfigsContext from './NavigationConfigsContext';
import useNavigationDrawerState from './useNavigationDrawerState';
import logEventToUnifiedLogger from '../utils/logEventToUnifiedLogger';
import { getEventBasePath } from '../utils/getBasePaths';
import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';
import { TrackerClientRequest } from '../event/eventConstants';
import { RailProvider } from '../layout/providers/RailProvider';
import getRobloxEnvironment from '../utils/getRobloxEnvironment';

export type NavigationConfigsProviderProps = {
  environment: TTargetEnvironment;
  robloxEnvironment?: TRobloxEnvironment;
  target: TBuildTarget;
  currentProduct: TProductKey;
  drawerVariant?: 'fullScreen' | 'belowAppBar';
  children?: React.ReactNode;
  compactBreakpoint?: number;
};

const NavigationConfigsProvider: FunctionComponent<NavigationConfigsProviderProps> = ({
  environment,
  robloxEnvironment,
  target,
  currentProduct,
  compactBreakpoint,
  drawerVariant = 'fullScreen',
  children,
}) => {
  const isCompact = useMediaQuery((theme) => theme.breakpoints.down(compactBreakpoint || 'Large'));
  const robloxEnv = robloxEnvironment ?? getRobloxEnvironment(environment);
  const [navigationFlags, setNavigationFlags] = useState<TNavigationIXPResults>({
    enableAssistant: false,
    disableProducts: ['CommunityEvents'],
    enableLuobu: false,
    creatorEventsVariant: null,
    enableCourses: false,
    enableReverseDelegation: false,
    enableReverseDelegationShadow: false,
  });
  const [navigationDropdownTabs, setNavigationDropdownTabs] = useState<TProductKey[]>([
    'Documentation',
    'Explore',
  ]);

  useIsomorphicLayoutEffect(() => {
    const {
      creatorEventsVariant,
      enableAssistant,
      enableCourses,
      enableReverseDelegation,
      enableReverseDelegationShadow,
    } = getNavigationIXPParams(target, environment);
    setNavigationFlags({
      ...navigationFlags,
      creatorEventsVariant,
      enableAssistant,
      enableCourses,
      enableReverseDelegation,
      enableReverseDelegationShadow,
    });
    if (creatorEventsVariant === 'dropdown') {
      setNavigationDropdownTabs((dropdownTabs) => [...dropdownTabs, 'Community']);
    }
  }, []);

  const unifiedLoggerClient = useMemo(() => {
    const eventBaseUrl = getEventBasePath(target, environment);
    return new UnifiedLogger({
      eventBaseUrl,
      product: 'CreatorHubShell',
      sessionProductGroup: 'CreatorHub',
    });
  }, [environment, target]);

  const sendEvent = useCallback(
    (clientRequest: TrackerClientRequest) => {
      logEventToUnifiedLogger(unifiedLoggerClient, currentProduct, clientRequest);
    },
    [currentProduct, unifiedLoggerClient],
  );

  const drawerState = useNavigationDrawerState();

  const value = useMemo(
    () => ({
      environment,
      robloxEnvironment: robloxEnv,
      target,
      currentProduct,
      ...navigationFlags,
      enableCourses: navigationFlags.enableCourses || false,
      enableNotificationTrayV2: true, // TODO @neoxu(10/23/2025): Remove this once the notification tray v2 is fully released. Enable for none-prod env
      enableReverseDelegation: navigationFlags.enableReverseDelegation ?? false,
      enableReverseDelegationShadow: navigationFlags.enableReverseDelegationShadow ?? false,
      drawerVariant,
      navigationDropdownTabs,
      isCompact,
      ...drawerState,
      sendEvent,
    }),
    [
      environment,
      robloxEnv,
      target,
      currentProduct,
      navigationFlags,
      drawerVariant,
      navigationDropdownTabs,
      isCompact,
      drawerState,
      sendEvent,
    ],
  );
  return (
    <NavigationConfigsContext.Provider value={value}>
      <RailProvider>{children}</RailProvider>
    </NavigationConfigsContext.Provider>
  );
};

export default NavigationConfigsProvider;
